"use client";

import { useMemo, useState } from "react";
import {
  BadgePercent,
  Package,
  ShieldCheck,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trackEvent } from "@/lib/analytics";
import { useI18n } from "@/components/i18n-provider";
import {
  buildOfferCatalog,
  type OfferItem,
} from "@/components/offer/offer-catalog";
import {
  OfferProductCard,
  type OfferCardLabels,
} from "@/components/offer/offer-product-card";

const REASON_ICONS: LucideIcon[] = [
  BadgePercent,
  Package,
  TrendingUp,
  ShieldCheck,
];

function ProductGrid({
  items,
  labels,
  tab,
  sourcePage,
}: {
  items: OfferItem[];
  labels: OfferCardLabels;
  tab: string;
  sourcePage: string;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <OfferProductCard
          key={item.id}
          item={item}
          labels={labels}
          tab={tab}
          sourcePage={sourcePage}
        />
      ))}
    </div>
  );
}

function SectionGroup({
  heading,
  viewAllLabel,
  onViewAll,
  children,
}: {
  heading: string;
  viewAllLabel: string;
  onViewAll: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-text-heading">{heading}</h3>
        <button
          type="button"
          onClick={onViewAll}
          className="text-sm font-medium text-brand-primary hover:underline"
        >
          {viewAllLabel}
        </button>
      </div>
      {children}
    </section>
  );
}

function WhyBundleRail({ onCompare }: { onCompare: () => void }) {
  const { dictionary } = useI18n();
  const copy = dictionary.dashboard.offerPage.whyBundle;

  return (
    <aside className="space-y-5 rounded-2xl border border-border-primary bg-card p-5 lg:sticky lg:top-4 lg:self-start">
      <h3 className="text-lg font-semibold text-text-heading">{copy.title}</h3>
      <ul className="space-y-4">
        {copy.reasons.map((reason, index) => {
          const Icon = REASON_ICONS[index % REASON_ICONS.length];
          return (
            <li key={reason.title} className="flex gap-3">
              <span className="flex size-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand-primary/10 text-brand-primary">
                <Icon className="size-4" aria-hidden="true" />
              </span>
              <div className="space-y-0.5">
                <p className="text-sm font-semibold text-text-heading">
                  {reason.title}
                </p>
                <p className="text-sm leading-6 text-text-muted">
                  {reason.body}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
      <Button
        type="button"
        variant="outline"
        onClick={onCompare}
        className="min-h-11 w-full border-border-primary text-text-heading"
      >
        {copy.compareCta}
      </Button>
    </aside>
  );
}

export function OfferShowcase({
  publicMode = false,
}: {
  publicMode?: boolean;
}) {
  const { locale, dictionary } = useI18n();
  const offerCopy = dictionary.dashboard.offerPage;
  const [tab, setTab] = useState("featured");

  const sourcePage = publicMode ? "offer_public" : "dashboard_offer";

  const handleTabChange = (value: string) => {
    setTab(value);
    trackEvent("offer_tab_change", {
      tab: value,
      source_page: sourcePage,
    });
  };

  const catalog = useMemo(
    () => buildOfferCatalog({ locale, dictionary, gateToSignUp: publicMode }),
    [locale, dictionary, publicMode],
  );

  const labels: OfferCardLabels = {
    freeBadge: offerCopy.products.freeBadge,
    deprecatedBadge: offerCopy.products.deprecatedBadge,
  };

  const featuredPaid = catalog.modules.filter((item) => item.featured);

  const tabs = [
    { value: "featured", label: offerCopy.tabs.featured },
    { value: "free", label: offerCopy.tabs.freeTools },
    { value: "modules", label: offerCopy.tabs.modules },
    { value: "all", label: offerCopy.tabs.all },
  ];

  return (
    <Tabs value={tab} onValueChange={handleTabChange} className="gap-6">
      <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-none border-b border-border-primary bg-transparent p-0">
        {tabs.map((entry) => (
          <TabsTrigger
            key={entry.value}
            value={entry.value}
            className="flex-none rounded-none border-0 border-b-2 border-transparent px-4 py-2.5 text-sm font-medium text-text-muted data-[state=active]:border-brand-primary data-[state=active]:bg-transparent data-[state=active]:text-brand-primary data-[state=active]:shadow-none"
          >
            {entry.label}
          </TabsTrigger>
        ))}
      </TabsList>

      <div className="grid gap-6 lg:grid-cols-[1fr_300px] lg:items-start">
        <div className="space-y-8">
          {tab === "featured" ? (
            <>
              <SectionGroup
                heading={offerCopy.free.heading}
                viewAllLabel={offerCopy.viewAllFree}
                onViewAll={() => handleTabChange("free")}
              >
                <ProductGrid items={catalog.free} labels={labels} tab={tab} sourcePage={sourcePage} />
              </SectionGroup>
              <SectionGroup
                heading={offerCopy.paid.heading}
                viewAllLabel={offerCopy.viewAllPaid}
                onViewAll={() => handleTabChange("modules")}
              >
                <ProductGrid items={featuredPaid} labels={labels} tab={tab} sourcePage={sourcePage} />
              </SectionGroup>
            </>
          ) : null}

          {tab === "free" ? (
            <SectionGroup
              heading={offerCopy.free.heading}
              viewAllLabel={offerCopy.tabs.all}
              onViewAll={() => handleTabChange("all")}
            >
              <ProductGrid items={catalog.free} labels={labels} tab={tab} sourcePage={sourcePage} />
            </SectionGroup>
          ) : null}

          {tab === "modules" ? (
            <SectionGroup
              heading={offerCopy.tabs.modules}
              viewAllLabel={offerCopy.tabs.all}
              onViewAll={() => handleTabChange("all")}
            >
              <ProductGrid items={catalog.modules} labels={labels} tab={tab} sourcePage={sourcePage} />
            </SectionGroup>
          ) : null}

          {tab === "all" ? (
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-text-heading">
                {offerCopy.tabs.all}
              </h3>
              <ProductGrid items={catalog.all} labels={labels} tab={tab} sourcePage={sourcePage} />
            </section>
          ) : null}
        </div>

        <WhyBundleRail onCompare={() => handleTabChange("all")} />
      </div>
    </Tabs>
  );
}
